/**
 * useObd — drives the ELM327 Web Bluetooth scan lifecycle for one vehicle and
 * persists the result. Surfaces an 'unsupported' status on platforms without
 * Web Bluetooth (iOS Safari) so the UI can show the Bluefy / Android guidance.
 */

import { useCallback, useRef, useState } from 'react';
import type { ObdScanResult } from '@tendedloop/shared';
import { Elm327Transport, isWebBluetoothAvailable } from '@/lib/obd/transport';
import { runDiagnosticScan, type ScanProgress } from '@/lib/obd/scanner';
import { ApiError, saveObdScan } from '@/lib/api';

export type ObdStatus = 'unsupported' | 'idle' | 'connecting' | 'scanning' | 'done' | 'error';

export function useObd(vehicleId: string) {
  const [status, setStatus] = useState<ObdStatus>(isWebBluetoothAvailable() ? 'idle' : 'unsupported');
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [result, setResult] = useState<ObdScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const transportRef = useRef<Elm327Transport | null>(null);

  const connectAndScan = useCallback(async () => {
    if (!isWebBluetoothAvailable()) {
      setStatus('unsupported');
      return;
    }
    setError(null);
    setResult(null);
    setSaved(false);

    const transport = new Elm327Transport();
    transportRef.current = transport;
    try {
      setStatus('connecting');
      await transport.connect();
      setStatus('scanning');
      const scan = await runDiagnosticScan(transport, { onProgress: setProgress });
      setResult(scan);
      setStatus('done');
      // Best-effort persist — the scan is still shown even if the save fails.
      try {
        await saveObdScan(vehicleId, scan);
        setSaved(true);
      } catch {
        setSaved(false);
      }
    } catch (err) {
      // User-cancelled chooser surfaces as a benign abort, not an error screen.
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Scan failed';
      if (/cancel|chooser|user gesture|NotFoundError/i.test(msg)) {
        setStatus('idle');
      } else {
        setError(msg);
        setStatus('error');
      }
    } finally {
      transport.disconnect();
    }
  }, [vehicleId]);

  const reset = useCallback(() => {
    transportRef.current?.disconnect();
    setStatus(isWebBluetoothAvailable() ? 'idle' : 'unsupported');
    setProgress(null);
    setResult(null);
    setError(null);
    setSaved(false);
  }, []);

  return { status, progress, result, error, saved, connectAndScan, reset };
}
